import { type KeyboardEvent, type ReactNode } from 'react'

export interface TabItem<T extends string> {
  id: T
  label: string
  badge?: ReactNode
  disabled?: boolean
}

export interface TabsProps<T extends string> {
  tabs: TabItem<T>[]
  value: T
  onChange: (id: T) => void
  ariaLabel: string
  /** the id prefix of the panels, for aria-controls */
  panelId?: string
}

/** Horizontal tabs with ←/→ keyboard moves (the panel sheets' look: underline in the primary colour). */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  panelId = 'panel',
}: TabsProps<T>) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const enabled = tabs.filter((t) => !t.disabled)
    const i = enabled.findIndex((t) => t.id === value)
    if (e.key === 'ArrowRight') onChange(enabled[(i + 1) % enabled.length]!.id)
    else if (e.key === 'ArrowLeft') onChange(enabled[(i - 1 + enabled.length) % enabled.length]!.id)
    else return
    e.preventDefault()
  }
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKey}
      className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-700"
    >
      {tabs.map((t) => {
        const active = t.id === value
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`${panelId}-tab-${t.id}`}
            aria-selected={active}
            aria-controls={`${panelId}-${t.id}`}
            tabIndex={active ? 0 : -1}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition disabled:opacity-40 ${active ? 'border-primary text-primary' : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'}`}
          >
            {t.label}
            {t.badge !== undefined && <span className="badge badge-gray">{t.badge}</span>}
          </button>
        )
      })}
    </div>
  )
}

export function TabPanel({
  id,
  active,
  children,
  panelId = 'panel',
}: {
  id: string
  active: boolean
  children: ReactNode
  panelId?: string
}) {
  if (!active) return null
  return (
    <div role="tabpanel" id={`${panelId}-${id}`} aria-labelledby={`${panelId}-tab-${id}`} className="pt-4">
      {children}
    </div>
  )
}
